/**
 * useEvents Hook
 *
 * Fetches calendar events for the visible date range.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import type { EventInstance } from '../types';

export function useEvents(rangeStart: Date | null, rangeEnd: Date | null, calendarId?: string) {
  const [events, setEvents] = useState<EventInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startIso = useMemo(() => rangeStart?.toISOString() ?? null, [rangeStart?.getTime()]);
  const endIso = useMemo(() => rangeEnd?.toISOString() ?? null, [rangeEnd?.getTime()]);

  const fetchEvents = useCallback(async () => {
    if (!startIso || !endIso) return;

    try {
      setLoading(true);
      const result = await api.listCalendarEvents({
        rangeStart: startIso,
        rangeEnd: endIso,
        calendarId,
      });
      setEvents(result.events);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [startIso, endIso, calendarId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
  };
}
